import {
  Badge,
  Button,
  Callout,
  Checkbox,
  CodeChip,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Field,
  Input,
  NativeSelect,
  Textarea,
  Tooltip,
  cn,
  useToast,
} from '@provenance/design';
import { todayCalendarDate } from '@provenance/shared';
import type { DocumentDetail, PageKind, SlotResolution, SupportingStampPlacement } from '@provenance/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Eye, ExternalLink, Move, Stamp } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ApiError, api } from '../../lib/api';
import { PageMap, pageKindsBody } from './PageMap';
import { StampPlacer } from './StampPlacer';

/**
 * ════════════════════════════════════════════════════════════════════════════
 * THE APPROVE DIALOG
 *
 * The moment a chamber's legal authority is applied to a document. Everything in the
 * product converges here, and the dialog is built around four things (spec §7.1):
 *
 *  1. THE CERTIFICATE NUMBER IS SHOWN, NOT ENTERED. It is generated from the chamber's
 *     configured format. The preview here is exactly what will print — but it is a
 *     preview, not a reservation: whoever commits first gets it, and the dialog says so
 *     rather than pretending the number is held.
 *
 *  2. ONE CONTROL PER CONFIGURED SEAL SLOT. Not a single "embed chamber stamp" checkbox.
 *     A document type may require two seals, and each is independently swappable to
 *     another active seal from the library.
 *
 *  3. THE AI FLAGS STAY VISIBLE. They do not block, but an operator approving past a
 *     failed check has to tick an acknowledgement — which is recorded in the audit trail,
 *     because "approved despite flagged mismatch X" is a legitimate and *visible*
 *     outcome (§8.4), not something to bury.
 *
 *  4. NUDGE, DON'T RECONFIGURE. Per-slot x/y adjustment for a one-off correction, which
 *     never writes back to the saved stamping configuration.
 * ════════════════════════════════════════════════════════════════════════════
 */
export function ApproveDialog({
  document: doc,
  open,
  onOpenChange,
}: {
  document: DocumentDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const context = doc.approvalContext;

  // The operator's own day. `toISOString().slice(0, 10)` is the day in UTC, which in
  // Brisbane is yesterday for the first ten hours of every day — so a chamber approving
  // at 00:30 on the 23rd got a certificate dated the 22nd, drawn from the 22nd's sequence.
  const [certificateDate, setCertificateDate] = useState(() => todayCalendarDate());
  // Chambers stamp retrospectively — a shipment that sailed last week still needs a
  // certificate dated when it sailed. So an unusual date is FLAGGED, never blocked: the
  // operator knows the circumstances and the system does not.
  const dateNote = useMemo(() => dateWarning(certificateDate, doc.receivedAt), [certificateDate, doc.receivedAt]);
  const [signatureProfileId, setSignatureProfileId] = useState(
    () => context?.signatureProfiles.find((p) => p.isDefault)?.id ?? context?.signatureProfiles[0]?.id ?? '',
  );
  const [sealChoices, setSealChoices] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      (context?.slots ?? [])
        .filter((slot) => slot.slotType === 'SEAL' && slot.defaultSealId)
        .map((slot) => [slot.id, slot.defaultSealId!]),
    ),
  );
  const [nudges, setNudges] = useState<Record<string, { x: number; y: number }>>({});
  const [showNudges, setShowNudges] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [supportingPlacements, setSupportingPlacements] = useState<
    Record<string, SupportingStampPlacement>
  >({});
  const [operatorNote, setOperatorNote] = useState('');
  // The operator's corrections to the page map, by page number (T-137).
  const [pageOverrides, setPageOverrides] = useState<Record<number, PageKind>>({});
  const pageKinds = useMemo(() => pageKindsBody(pageOverrides), [pageOverrides]);

  // Only FAIL findings require an explicit acknowledgement. Warnings are visible but
  // making an operator tick a box for every formatting difference would make the tick
  // meaningless.
  const blockingFlags = useMemo(
    () => doc.checks.results.filter((r) => r.status === 'FAIL'),
    [doc.checks.results],
  );
  const warnings = useMemo(
    () => doc.checks.results.filter((r) => r.status === 'WARNING'),
    [doc.checks.results],
  );

  const sealSlots = (context?.slots ?? []).filter((slot) => slot.slotType === 'SEAL');
  const otherSlots = (context?.slots ?? []).filter((slot) => slot.slotType !== 'SEAL');
  const supportingToStamp = doc.supportingDocuments.filter((s) => s.requestSignatureAndStamp);

  const needsAcknowledgement = blockingFlags.length > 0;
  const missingSeal = sealSlots.some((slot) => slot.required && !sealChoices[slot.id]);
  const canSubmit =
    Boolean(signatureProfileId) &&
    Boolean(certificateDate) &&
    !missingSeal &&
    (context?.blockers.length ?? 0) === 0 &&
    (!needsAcknowledgement || acknowledged);

  /** What the operator currently has set. Used by both the preview and the approval. */
  const slotResolutions: SlotResolution[] = useMemo(
    () =>
      (context?.slots ?? []).map((slot) => ({
        slotId: slot.id,
        sealId: sealChoices[slot.id] ?? null,
        offsetX: nudges[slot.id]?.x ?? 0,
        offsetY: nudges[slot.id]?.y ?? 0,
      })),
    [context?.slots, sealChoices, nudges],
  );

  /**
   * Seeing the certificate before committing to it.
   *
   * Approval is the irreversible step — it draws a number §2.2 forbids ever reusing — so
   * the operator should not have to approve in order to find out what they are about to
   * issue. This renders their own PDF through the real engine, watermarked PREVIEW, and
   * reserves nothing.
   *
   * On demand, not live: every render is a full server-side PDF composite, and doing it on
   * every nudge keystroke would turn the dialog into a load test.
   */
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const previewObjectUrl = useRef<string | null>(null);
  // Revoked on unmount: a blob URL pins the whole PDF in memory until it is.
  useEffect(
    () => () => {
      if (previewObjectUrl.current) URL.revokeObjectURL(previewObjectUrl.current);
    },
    [],
  );

  const preview = useMutation({
    mutationFn: () =>
      api.blob(`/api/console/documents/${doc.id}/stamp-preview`, {
        certificateDate,
        signatureProfileId,
        slotResolutions,
        pageKinds,
      }),
    onSuccess: (blob) => {
      if (previewObjectUrl.current) URL.revokeObjectURL(previewObjectUrl.current);
      previewObjectUrl.current = URL.createObjectURL(blob);
      setPreviewUrl(previewObjectUrl.current);
      setPreviewError(null);
    },
    onError: (error) =>
      setPreviewError(
        error instanceof ApiError
          ? error.message
          : 'The preview could not be rendered. Nothing has been issued.',
      ),
  });

  const approve = useMutation({
    mutationFn: async () => {
      return api.post<{ certificateNo: string; verificationCode: string }>(
        `/api/console/documents/${doc.id}/approve`,
        {
          certificateDate,
          signatureProfileId,
          slotResolutions,
          pageKinds,
          supportingStampPlacements: Object.values(supportingPlacements),
          acknowledgedFlags: blockingFlags.map((f) => f.checkType),
          operatorNote: operatorNote.trim() || null,
        },
      );
    },
    onSuccess: (result) => {
      toast.toast({
        tone: 'success',
        title: `Certificate ${result.certificateNo} issued`,
        description: `Verification code ${result.verificationCode}. The stamped PDF is being generated now.`,
      });
      onOpenChange(false);
      void queryClient.invalidateQueries({ queryKey: ['document', doc.id] });
      void queryClient.invalidateQueries({ queryKey: ['documents'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
    onError: (error) => {
      toast.error(
        'Could not approve this certificate',
        error instanceof ApiError ? error.message : 'Something went wrong. Try again.',
      );
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg">
        <DialogHeader
          eyebrow={`${doc.documentTypeName} · ${doc.senderReference}`}
          title="Approve and issue"
          description={`This applies ${doc.organisationName}'s certificate under your chamber's authority. It cannot be undone — an issued certificate can only be revoked.`}
        />

        <DialogBody className="flex flex-col gap-5">
          {(context?.blockers.length ?? 0) > 0 && (
            <Callout tone="stop" icon={<AlertTriangle className="size-4" />} title="Not ready to issue">
              <ul className="m-0 list-disc pl-4">
                {context!.blockers.map((blocker) => (
                  <li key={blocker.code}>{blocker.message}</li>
                ))}
              </ul>
            </Callout>
          )}

          {/* What it will look like. Rendered by the engine that stamps real certificates,
              from the operator's own PDF, with everything currently set in this dialog. */}
          {previewError && (
            <Callout
              tone="warn"
              icon={<AlertTriangle className="size-4" />}
              title="The preview could not be rendered"
            >
              {previewError}{' '}
              <span className="text-[var(--pv-muted)]">
                Nothing has been issued. If this is the document itself, approving would fail the
                same way.
              </span>
            </Callout>
          )}

          {previewUrl && (
            <div className="flex flex-col gap-2 rounded-[var(--pv-radius-lg)] border border-[var(--pv-line-strong)] p-3">
              <div className="flex flex-wrap items-center gap-2">
                <p className="min-w-[16ch] flex-1 [font-size:12.5px] text-[var(--pv-muted)]">
                  Watermarked <strong>PREVIEW</strong>. Nothing has been issued and no number has
                  been taken — {context?.nextCertificateNoPreview ?? 'the number'} is still only
                  what you would get.
                </p>
                <Button
                  variant="ghost"
                  size="sm"
                  asChild
                  leadingIcon={<ExternalLink className="size-3.5" />}
                >
                  <a href={previewUrl} target="_blank" rel="noreferrer">
                    Open full size
                  </a>
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  loading={preview.isPending}
                  leadingIcon={<Eye className="size-3.5" />}
                  onClick={() => preview.mutate()}
                >
                  Refresh
                </Button>
              </div>
              <iframe
                key={previewUrl}
                src={`${previewUrl}#toolbar=0&navpanes=0`}
                title="Certificate preview"
                className="h-[420px] w-full rounded-[var(--pv-radius)] border border-[var(--pv-line)] bg-white"
              />
            </div>
          )}

          {/* The number. Shown prominently because it is the one thing on this dialog
              that becomes permanent and legally significant. */}
          <div className="rounded-[var(--pv-radius-lg)] border border-[var(--pv-leaf)] bg-[var(--pv-leaf-soft)] p-4">
            <p className="pv-eyebrow text-[var(--pv-leaf-ink)]">
              {doc.submissionType === 'AMENDMENT'
                ? 'Amendment of ' + (doc.originalCertificateNo ?? 'the original')
                : 'Certificate number'}
            </p>
            <p className="pv-mono mt-1.5 [font-size:26px] font-semibold leading-none tracking-[0.01em]">
              {doc.submissionType === 'AMENDMENT'
                ? `${doc.originalCertificateNo ?? '—'} + amendment suffix`
                : (context?.nextCertificateNoPreview ?? '—')}
            </p>
            <p className="mt-2 [font-size:var(--pv-text-xs)] leading-relaxed text-[var(--pv-leaf-ink)]/80">
              {doc.submissionType === 'AMENDMENT'
                ? 'An amendment keeps the original number so it stays matchable at the border, and consumes no new sequence value.'
                : 'Generated from your chamber’s configured format. Another operator approving first would take this number and you would get the next one.'}
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label="Certificate date"
              required
              help="Prints on the certificate and sets which sequence period the number comes from. Back-date it to issue retrospectively."
            >
              <Input
                type="date"
                value={certificateDate}
                onChange={(e) => setCertificateDate(e.target.value)}
              />
              {dateNote && (
                <p className="mt-1.5 flex items-start gap-1.5 [font-size:var(--pv-text-2xs)] text-[var(--pv-clay-deep)]">
                  <AlertTriangle className="mt-px size-3 shrink-0" aria-hidden />
                  <span>{dateNote}</span>
                </p>
              )}
            </Field>

            <Field label="Signature" required help="Your signature and designation print on the certificate.">
              <NativeSelect
                value={signatureProfileId}
                onChange={(e) => setSignatureProfileId(e.target.value)}
              >
                {(context?.signatureProfiles ?? []).map((profile) => (
                  <option key={profile.id} value={profile.id}>
                    {profile.signatoryName} — {profile.designation}
                  </option>
                ))}
              </NativeSelect>
            </Field>
          </div>

          <PageMap documentId={doc.id} overrides={pageOverrides} onChange={setPageOverrides} />

          {/* One control per seal slot. A document type with two seals gets two. */}
          {sealSlots.length > 0 && (
            <section>
              <h4 className="[font-size:var(--pv-text-h3)] font-bold">
                {sealSlots.length === 1 ? 'Seal' : `${sealSlots.length} seals`}
              </h4>
              <p className="mt-1 [font-size:var(--pv-text-xs)] text-[var(--pv-muted)]">
                This document type requires {sealSlots.length === 1 ? 'one seal' : `${sealSlots.length} seals`}.
                Each is pre-filled from your configuration and can be swapped to another active seal.
              </p>
              <div className="mt-3 flex flex-col gap-3">
                {sealSlots.map((slot) => (
                  <div
                    key={slot.id}
                    className="flex flex-wrap items-center gap-3 rounded-[var(--pv-radius)] border border-[var(--pv-line)] bg-[var(--pv-card-alt)] p-3"
                  >
                    <Stamp aria-hidden className="size-4 shrink-0 text-[var(--pv-leaf-deep)]" />
                    <div className="min-w-0 flex-1">
                      <p className="[font-size:13px] font-medium">{slot.label ?? 'Seal'}</p>
                      <p className="[font-size:var(--pv-text-2xs)] text-[var(--pv-faint)]">Page {slot.page}</p>
                    </div>
                    <NativeSelect
                      value={sealChoices[slot.id] ?? ''}
                      onChange={(e) =>
                        setSealChoices((prev) => ({ ...prev, [slot.id]: e.target.value }))
                      }
                      className="w-auto min-w-[14rem]"
                      aria-label={`Seal for ${slot.label ?? 'this slot'}`}
                    >
                      <option value="">Choose a seal…</option>
                      {(context?.availableSeals ?? []).map((seal) => (
                        <option key={seal.id} value={seal.id}>
                          {seal.name}
                        </option>
                      ))}
                    </NativeSelect>
                    {sealChoices[slot.id] && (
                      <img
                        src={context?.availableSeals.find((s) => s.id === sealChoices[slot.id])?.imageUrl ?? ''}
                        alt=""
                        className="h-8 max-w-[120px] object-contain"
                      />
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Everything else that gets placed, listed so the operator knows what will
              appear without having to trust the configuration blindly. */}
          {otherSlots.length > 0 && (
            <section>
              <div className="flex items-baseline justify-between gap-3">
                <h4 className="[font-size:var(--pv-text-h3)] font-bold">What else will print</h4>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowNudges((v) => !v)}
                  leadingIcon={<Move className="size-3.5" />}
                >
                  {showNudges ? 'Hide' : 'Adjust'} positions
                </Button>
              </div>
              <ul className="mt-2 flex list-none flex-col gap-0 p-0">
                {otherSlots.map((slot) => (
                  <li
                    key={slot.id}
                    className="flex flex-wrap items-center gap-3 border-b border-[var(--pv-line)] py-2 last:border-b-0"
                  >
                    <span className="min-w-0 flex-1 [font-size:13px]">
                      {slot.label ?? slot.slotType}
                      <span className="ml-2 [font-size:var(--pv-text-2xs)] text-[var(--pv-faint)]">
                        page {slot.page}
                      </span>
                    </span>
                    {slot.resolvedPreview && <CodeChip>{slot.resolvedPreview}</CodeChip>}
                    {showNudges && (
                      <span className="flex items-center gap-1.5">
                        <NudgeInput
                          label={`${slot.label ?? slot.slotType} horizontal offset`}
                          axis="x"
                          value={nudges[slot.id]?.x ?? 0}
                          onChange={(value) =>
                            setNudges((prev) => ({
                              ...prev,
                              [slot.id]: { x: value, y: prev[slot.id]?.y ?? 0 },
                            }))
                          }
                        />
                        <NudgeInput
                          label={`${slot.label ?? slot.slotType} vertical offset`}
                          axis="y"
                          value={nudges[slot.id]?.y ?? 0}
                          onChange={(value) =>
                            setNudges((prev) => ({
                              ...prev,
                              [slot.id]: { x: prev[slot.id]?.x ?? 0, y: value },
                            }))
                          }
                        />
                      </span>
                    )}
                  </li>
                ))}
              </ul>
              {showNudges && (
                <p className="mt-2 [font-size:var(--pv-text-2xs)] leading-relaxed text-[var(--pv-faint)]">
                  Offsets are in points, positive is right and up, and apply to this certificate
                  only. Your saved stamping configuration is untouched.
                </p>
              )}
            </section>
          )}

          {supportingToStamp.length > 0 && (
            <section>
              <h4 className="[font-size:var(--pv-text-h3)] font-bold">
                Supporting documents to stamp
              </h4>
              <p className="mt-1 [font-size:var(--pv-text-xs)] leading-relaxed text-[var(--pv-muted)]">
                The exporter asked for {supportingToStamp.length === 1 ? 'this document' : 'these documents'} to
                be stamped too. Unlike the certificate, these have no fixed layout — click where the
                stamp should go, or leave it and your chamber's default position is used.
              </p>
              <div className="mt-3 flex flex-col gap-3">
                {supportingToStamp.map((supporting) => (
                  <StampPlacer
                    key={supporting.id}
                    document={supporting}
                    placement={supportingPlacements[supporting.id]}
                    onChange={(placement) =>
                      setSupportingPlacements((prev) => {
                        const next = { ...prev };
                        if (placement) next[supporting.id] = placement;
                        else delete next[supporting.id];
                        return next;
                      })
                    }
                  />
                ))}
              </div>
            </section>
          )}

          {/* The flags. Still visible at the moment of decision, per §7.1. */}
          {(blockingFlags.length > 0 || warnings.length > 0) && (
            <section
              className={cn(
                'rounded-[var(--pv-radius)] border p-4',
                blockingFlags.length > 0
                  ? 'border-[var(--pv-stop-line)] bg-[var(--pv-stop-bg)]'
                  : 'border-[var(--pv-warn-line)] bg-[var(--pv-warn-bg)]',
              )}
            >
              <div className="flex items-center gap-2">
                <AlertTriangle
                  aria-hidden
                  className={cn(
                    'size-4',
                    blockingFlags.length > 0 ? 'text-[var(--pv-stop)]' : 'text-[var(--pv-warn)]',
                  )}
                />
                <h4 className="[font-size:13.5px] font-semibold">
                  The completeness check flagged {blockingFlags.length + warnings.length} thing
                  {blockingFlags.length + warnings.length === 1 ? '' : 's'}
                </h4>
              </div>
              <ul className="mt-2.5 flex list-none flex-col gap-2 p-0">
                {[...blockingFlags, ...warnings].map((flag) => (
                  <li key={flag.id} className="flex gap-2 [font-size:13px] leading-relaxed">
                    <Badge tone={flag.status === 'FAIL' ? 'stop' : 'warn'} size="sm">
                      {flag.status === 'FAIL' ? 'Check' : 'Note'}
                    </Badge>
                    <span className="min-w-0">
                      <strong className="font-semibold">{flag.title}.</strong> {flag.message}
                    </span>
                  </li>
                ))}
              </ul>
              {needsAcknowledgement && (
                <div className="mt-3.5 border-t border-[var(--pv-stop-line)] pt-3">
                  <Checkbox
                    checked={acknowledged}
                    onCheckedChange={(v) => setAcknowledged(v === true)}
                    label="I have reviewed these and am issuing the certificate anyway"
                    description="Recorded against this certificate with your name, so the decision is traceable."
                  />
                </div>
              )}
            </section>
          )}

          <Field
            label="Note"
            help="Optional. Recorded in the audit trail — useful when you have approved past a flag."
          >
            <Textarea
              value={operatorNote}
              onChange={(e) => setOperatorNote(e.target.value)}
              placeholder="Anything worth recording about this decision"
              rows={2}
            />
          </Field>
        </DialogBody>

        <DialogFooter
          note={
            missingSeal
              ? 'Choose a seal for every required slot before issuing.'
              : needsAcknowledgement && !acknowledged
                ? 'Tick the acknowledgement above to issue despite the flagged checks.'
                : 'Preview costs nothing. The number is drawn and the PDF stamped the moment you press Issue.'
          }
        >
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          {/* Deliberately not disabled by `canSubmit`: seeing what is wrong is most useful
              precisely when something is not ready yet. */}
          <Button
            variant="secondary"
            loading={preview.isPending}
            disabled={!signatureProfileId || !certificateDate}
            leadingIcon={<Eye className="size-3.5" />}
            onClick={() => preview.mutate()}
          >
            {previewUrl ? 'Refresh preview' : 'Preview'}
          </Button>
          <Tooltip content={canSubmit ? '' : 'Complete the dialog first'}>
            <Button
              variant="primary"
              disabled={!canSubmit}
              loading={approve.isPending}
              onClick={() => approve.mutate()}
            >
              Issue the certificate
            </Button>
          </Tooltip>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * A one-off positional adjustment, in PDF points.
 *
 * Arrow keys step by 1pt because that is the precision a misplaced stamp actually needs —
 * the visual placement tool (todo/T-051) covers gross repositioning.
 */
function NudgeInput({
  label,
  axis,
  value,
  onChange,
}: {
  label: string;
  axis: 'x' | 'y';
  value: number;
  onChange: (value: number) => void;
}) {
  return (
    <label className="inline-flex items-center gap-1">
      <span className="sr-only">{label}</span>
      <span aria-hidden className="[font-size:var(--pv-text-xs)] text-[var(--pv-faint)]">
        {axis}
      </span>
      <input
        type="number"
        value={value}
        step={1}
        min={-200}
        max={200}
        onChange={(e) => onChange(Number(e.target.value))}
        className="pv-mono h-7 w-16 rounded-[var(--pv-radius-xs)] border border-[var(--pv-line-strong)] bg-white px-1.5 text-center [font-size:11.5px]"
      />
    </label>
  );
}

/**
 * What to say about a certificate date that looks unusual — and nothing at all when it
 * does not.
 *
 * Every case here is legitimate in the right circumstances, which is why none of them
 * block. A wrong YEAR is the one that would survive unnoticed onto a legal document and
 * only surface when a customs authority queried it, so it is worth a line of text.
 */
export function dateWarning(chosen: string, receivedAt: string): string | null {
  if (!chosen) return null;
  const date = new Date(`${chosen}T00:00:00`);
  if (Number.isNaN(date.getTime())) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const received = new Date(receivedAt);
  received.setHours(0, 0, 0, 0);

  if (date.getTime() > today.getTime()) {
    return 'That date is in the future. The certificate will be dated ahead of the day it was issued.';
  }

  const daysBack = Math.round((today.getTime() - date.getTime()) / 86_400_000);
  if (daysBack > 365) {
    return `That is over a year ago — check the year. The number will be drawn from the ${date.getFullYear()} sequence.`;
  }

  if (date.getTime() < received.getTime()) {
    return `That is before this request arrived on ${received.toLocaleDateString('en-AU')}. Fine if you are issuing retrospectively.`;
  }

  return null;
}
